import { ABP, ListService, PagedResultDto, TrackByService } from '@abp/ng.core';
import { Confirmation, ConfirmationService } from '@abp/ng.theme.shared';
import { DateAdapter } from '@abp/ng.theme.shared/extensions';
import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbDateAdapter } from '@ng-bootstrap/ng-bootstrap';
import { filter, finalize, switchMap, tap } from 'rxjs/operators';
import type {
  GetAssetEventsInput,
  AssetEventWithNavigationPropertiesDto,
} from '../../../proxy/asset-events/models';
import { AssetEventService } from '../../../proxy/asset-events/asset-event.service';

@Component({
  selector: 'lib-asset-event',
  changeDetection: ChangeDetectionStrategy.Default,
  providers: [ListService, { provide: NgbDateAdapter, useClass: DateAdapter }],
  templateUrl: './asset-event.component.html',
  styles: [],
})
export class AssetEventComponent implements OnInit {
  data: PagedResultDto<AssetEventWithNavigationPropertiesDto> = {
    items: [],
    totalCount: 0,
  };

  filters = {} as GetAssetEventsInput;

  form: FormGroup;

  isFiltersHidden = true;

  isModalBusy = false;

  isModalOpen = false;

  selected?: AssetEventWithNavigationPropertiesDto;

  constructor(
    public readonly list: ListService,
    public readonly track: TrackByService,
    public readonly service: AssetEventService,
    private confirmation: ConfirmationService,
    private fb: FormBuilder
  ) {}

  ngOnInit() {
    const getData = (query: ABP.PageQueryParams) =>
      this.service.getList({
        ...query,
        ...this.filters,
        filterText: query.filter,
      });

    const setData = (list: PagedResultDto<AssetEventWithNavigationPropertiesDto>) => (this.data = list);

    this.list.hookToQuery(getData).subscribe(setData);
  }

  clearFilters() {
    this.filters = {} as GetAssetEventsInput;
  }

  buildForm() {
    const {
      description,
      eventDateTime,
      assetId,
      eventTypeId,
      noteId,
    } = this.selected?.assetEvent || {};

    this.form = this.fb.group({
      description: [description ?? null, [Validators.required]],
      eventDateTime: [eventDateTime ? new Date(eventDateTime) : null, [Validators.required]],
      assetId: [assetId ?? null, [Validators.required]],
      eventTypeId: [eventTypeId ?? null, [Validators.required]],
      noteId: [noteId ?? null, []],
    });
  }

  hideForm() {
    this.isModalOpen = false;
    this.form.reset();
  }

  showForm() {
    this.buildForm();
    this.isModalOpen = true;
  }

  submitForm() {
    if (this.form.invalid) return;

    const request = this.selected
      ? this.service.update(this.selected.assetEvent.id, this.form.value)
      : this.service.create(this.form.value);

    this.isModalBusy = true;

    request
      .pipe(
        finalize(() => (this.isModalBusy = false)),
        tap(() => this.hideForm()),
      )
      .subscribe(this.list.get);
  }

  create() {
    this.selected = undefined;
    this.showForm();
  }

  update(record: AssetEventWithNavigationPropertiesDto) {
    this.selected = record;
    this.showForm();
  }

  delete(record: AssetEventWithNavigationPropertiesDto) {
    this.confirmation.warn(
      'Inventory::DeleteConfirmationMessage',
      'Inventory::AreYouSure',
      { messageLocalizationParams: [] }
    ).pipe(
      filter(status => status === Confirmation.Status.confirm),
      switchMap(() => this.service.delete(record.assetEvent.id)),
    ).subscribe(this.list.get);
  }
}
